import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Switch from "react-switch";
import Swal from "sweetalert2";
import "./rules.css";
import "../color_pallet/colorPallet.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";

const GAME_TITLE_MAX_LEN = 100;
const GAME_SUB_TITLE_MAX_LEN = 300;

const DEFAULT_ADDL_FIELDS = [
  { name: "first_name", type: "text", selected: false },
  { name: "last_name", type: "text", selected: false },
  { name: "employee_id", type: "text", selected: false },
  { name: "email", type: "email", selected: false },
  { name: "phone", type: "tel", selected: false },
  { name: "location", type: "text", selected: true },
];

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [gameTitle, setGameTitle] = useState("");
  const [gameSubTitle, setGameSubTitle] = useState("");
  const [additionalFieldsOn, setAdditionalFieldsOn] = useState(false);
  const [additionalNewFields, setAdditionalNewFields] = useState(DEFAULT_ADDL_FIELDS);
  const [saving, setSaving] = useState(false);

  const [colors, setColors] = useState({
    text_color: "#000000",
    button_color: "#ffffff",
    button_backgroundcolor: "#088e01",
  });

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;

    if ("game_page_title" in data || "game_title" in data || "landing_page_title" in data) {
      const t = String(data.game_page_title ?? data.game_title ?? data.landing_page_title ?? "");
      setGameTitle(t.slice(0, GAME_TITLE_MAX_LEN));
    }
    if ("game_page_sub_title" in data || "game_sub_title" in data) {
      setGameSubTitle(
        String(data.game_page_sub_title ?? data.game_sub_title ?? "").slice(0, GAME_SUB_TITLE_MAX_LEN)
      );
    }
    if ("additional_fields" in data) {
      setAdditionalFieldsOn(String(data.additional_fields) === "true");
    }
    if ("additional_new_fields" in data) {
      setAdditionalNewFields(
        Array.isArray(data.additional_new_fields) && data.additional_new_fields.length > 0
          ? data.additional_new_fields
          : DEFAULT_ADDL_FIELDS
      );
    }

    setColors((prev) => ({
      text_color: data.text_color ?? prev.text_color,
      button_color: data.button_color ?? data.button_text_color ?? prev.button_color,
      button_backgroundcolor:
        data.button_backgroundcolor ?? data.button_background_color ?? prev.button_backgroundcolor,
    }));
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleGameTitleChange = (val) => {
    let next = val;
    if (next.length > GAME_TITLE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Game title can be at most ${GAME_TITLE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, GAME_TITLE_MAX_LEN);
    }
    setGameTitle(next);
  };

  const handleGameSubTitleChange = (val) => {
    let next = val;
    if (next.length > GAME_SUB_TITLE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Game sub title can be at most ${GAME_SUB_TITLE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, GAME_SUB_TITLE_MAX_LEN);
    }
    setGameSubTitle(next);
  };

  const toggleFieldSelected = (index) => {
    setAdditionalNewFields((prev) =>
      prev.map((f, i) => (i === index ? { ...f, selected: !f.selected } : f))
    );
  };

  const handleRemoveField = (index) => {
    setAdditionalNewFields((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddField = async () => {
    const { value: name } = await Swal.fire({
      title: "Add input field",
      input: "text",
      inputLabel: "Field name",
      inputPlaceholder: "e.g. department",
      showCancelButton: true,
      inputValidator: (v) => (!String(v || "").trim() ? "Field name is required" : undefined),
    });
    if (!name) return;
    const cleaned = String(name).trim().toLowerCase().replace(/\s+/g, "_");
    if (additionalNewFields.some((f) => f.name === cleaned)) {
      Swal.fire("Duplicate", "A field with this name already exists.", "warning");
      return;
    }
    const type =
      cleaned === "email" ? "email" : cleaned === "phone" || cleaned === "mobile" ? "tel" : "text";
    setAdditionalNewFields((prev) => [...prev, { name: cleaned, type, selected: false }]);
  };

  const handleSubmit = async () => {
    if (!currentTheme || !adminToken) {
      Swal.fire("No theme", "Select a theme before saving.", "warning");
      return;
    }

    const payload = {
      data: {
        game_page_title: String(gameTitle ?? "").slice(0, GAME_TITLE_MAX_LEN),
        game_title: String(gameTitle ?? "").slice(0, GAME_TITLE_MAX_LEN),
        game_page_sub_title: String(gameSubTitle ?? "").slice(0, GAME_SUB_TITLE_MAX_LEN),
        game_sub_title: String(gameSubTitle ?? "").slice(0, GAME_SUB_TITLE_MAX_LEN),
        text_color: colors.text_color,
        button_color: colors.button_color,
        button_text_color: colors.button_color,
        button_backgroundcolor: colors.button_backgroundcolor,
        button_background_color: colors.button_backgroundcolor,
        additional_fields: additionalFieldsOn ? "true" : "false",
        additional_new_fields: additionalNewFields,
      },
      currentTheme,
    };

    setSaving(true);
    try {
      await updateThemeData({ payload, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", "Game settings saved successfully.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-center">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game Settings
                </div>

                <div className="form-group-v1">
                  <label>Game title</label>
                  <textarea
                    value={gameTitle}
                    onChange={(e) => handleGameTitleChange(e.target.value)}
                    placeholder="Game title"
                    className="form-control rules-textarea"
                    rows={2}
                    maxLength={GAME_TITLE_MAX_LEN}
                  />
                  <div className="code">* Maximum {GAME_TITLE_MAX_LEN} characters.</div>
                </div>

                <div className="form-group-v1">
                  <label>Game sub title</label>
                  <textarea
                    value={gameSubTitle}
                    onChange={(e) => handleGameSubTitleChange(e.target.value)}
                    placeholder="Game sub title"
                    className="form-control rules-textarea"
                    rows={4}
                    maxLength={GAME_SUB_TITLE_MAX_LEN}
                  />
                  <div className="code">* Maximum {GAME_SUB_TITLE_MAX_LEN} characters.</div>
                </div>

                <div className="form-group-v1 rules-toggle-row">
                  <label>Additional fields</label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={additionalFieldsOn}
                      onChange={setAdditionalFieldsOn}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>{additionalFieldsOn ? "Enabled" : "Disabled"}</span>
                  </div>
                </div>

                {additionalFieldsOn ? (
                  <div className="form-group-v1">
                    <h3 className="custom-rules-title">Form fields</h3>
                    <div className="code">* Click the dot to mark a field as required on the game form.</div>
                    {additionalNewFields.map((f, i) => (
                      <div className="rule-row rules-addl-row" key={`${f.name}-${i}`}>
                        <button
                          type="button"
                          className={`rules-field-dot${f.selected ? " rules-field-dot--on" : ""}`}
                          title={f.selected ? "Selected" : "Not selected"}
                          onClick={() => toggleFieldSelected(i)}
                          aria-label={`Toggle ${f.name}`}
                        />
                        <span className="rules-field-name">{f.name}</span>
                        <span className="rules-field-type">{f.type}</span>
                        <button
                          type="button"
                          className="rules-field-remove-btn"
                          onClick={() => handleRemoveField(i)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <div className="rules-addl-actions">
                      <button type="button" className="add-btn" onClick={handleAddField}>
                        + Add Input
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Colors
                </div>
                <div className="pallet-holder">
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.text_color}
                          onChange={handleColorChange("text_color")}
                        />
                        <label>Text Color</label>
                      </div>
                      <div className="color-code">{colors.text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_color}
                          onChange={handleColorChange("button_color")}
                        />
                        <label>Button Text Color</label>
                      </div>
                      <div className="color-code">{colors.button_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_backgroundcolor}
                          onChange={handleColorChange("button_backgroundcolor")}
                        />
                        <label>Button Background Color</label>
                      </div>
                      <div className="color-code">{colors.button_backgroundcolor}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
